import { Store } from '@ngrx/store';
import { BehaviorSubject, EMPTY, Observable, of } from 'rxjs';
import { first, map } from 'rxjs/operators';
import { IModelEntity, PITEventService, UtilsMisc } from '@pit/core';
import { EntityDataServiceAccessorService, IListResult } from '@pit/core-data';
import { EntityStoreFactory } from './entity.store.factory';
import { IEntityStoreFacade } from '../interfaces/entity.store.facae.interface';

export class EntityStoreFacade<T extends IModelEntity> implements IEntityStoreFacade<T>{

    private _isLoading: boolean = false;
    private _selectedItem: T = null;

    constructor(protected entityStoreFactory: EntityStoreFactory<T>, protected store: Store<T>,
        protected modelName: string, protected dataServiceAccessor: EntityDataServiceAccessorService, protected eventService: PITEventService) {

        this.isLoading$.subscribe(value => {
            this._isLoading = value;
            this.eventService.useMethod((value === true) ? 'busy.on' : 'busy.off');
        });
        this.selectedItem$.subscribe(
            item => (this._selectedItem = item)
        );
    }

    public get isLoading(): boolean {
        return this._isLoading;
    }

    public set isLoading(value: boolean) {
        if (value !== this.isLoading) {
            this._isLoading = value;

            this.store.dispatch(this.entityStoreFactory.getAction('set.loading')({ value: this.isLoading }));
        }
    }

    public get selectedItem(): T {
        return this._selectedItem;
    }

    public set selectedItem(value: T) {
        if (value !== this.selectedItem) {
            this._selectedItem = value;

            this.store.dispatch(this.entityStoreFactory.getAction('set.selected')({ item: this.selectedItem }));
        }
    }

    public get selectedItemId(): number {
        return this.selectedItem?.id;
    }

    public set selectedItemId(value: number) {
        if (value !== this.selectedItemId) {
            this.selectItemById$(value);
        }
    }

    public isLoading$: Observable<boolean> = this.store
        .select(this.entityStoreFactory.getSelector('get.loading'));

    public items$: Observable<T[]> = this.store
        .select(this.entityStoreFactory.getSelector('get.all.entities'));

    public selectedItem$: Observable<T> = this.store
        .select(this.entityStoreFactory.getSelector('get.selected.entity'));

    public selectedItemId$: Observable<number> = this.store
        .select(this.entityStoreFactory.getSelector('get.selected.entity.id'));

    public get updateSubject(): BehaviorSubject<any> {
        return this.entityStoreFactory.updateSubject;
    }

    public async getAllItemsAsync(): Promise<T[]> {
        return (await this.items$.pipe(first()).toPromise());
    }

    public getItemById$(id: number): Observable<T> {
        return this.store
            .select(this.entityStoreFactory.getSelector('get.entity.by.id')(id));
    }

    public async getItemByIdAsync(id: number): Promise<T> {
        let result: T = null;

        try {
            result = (await this.getItemById$(id).pipe(first()).toPromise());
        } catch (error) {
            this.eventService.useMethod('error.log', error);
        }

        return result;
    }

    public removeItem(item: T): void {
        if (item != null) {
            this.store.dispatch(this.entityStoreFactory.getAction('remove.single')({ item: item }));
        }
    }

    public deleteItem(item: T, showLoadingIndicator: boolean = true): void {
        this.store.dispatch(this.entityStoreFactory.getAction('delete.single')({ item: item, showLoadingIndicator: showLoadingIndicator }));
    }

    public async deleteItemAsync(item: T, showLoadingIndicator: boolean = true): Promise<void> {
        try {
            if (showLoadingIndicator === true) {
                this.eventService.useMethod('busy.on');
            }

            (((await this.dataServiceAccessor.getEntityDataService(this.entityStoreFactory.dataAccessName.toLowerCase()).deleteAsync(item)) as any) as T);
            this.removeItem(item);
        } catch (error) {
            throw error;
        } finally {
            if (showLoadingIndicator === true) {
                this.eventService.useMethod('busy.off');
            }
        }
    }

    public clear(): void {
        this.store.dispatch(this.entityStoreFactory.getAction('clear')());
    }

    public getItemCount$(): Observable<number> {
        return this.items$.pipe(
            map(items => {
                return items.length;
            })
        );
    }

    public async getItemCountAsync(): Promise<number> {
        return (await this.getItemCount$().pipe(first()).toPromise());
    }

    public loadItems$(showLoadingIndicator: boolean = true): Observable<T[]> {
        this.store.dispatch(this.entityStoreFactory.getAction('load.all')({ showLoadingIndicator: showLoadingIndicator }));

        return this.items$;
    }

    public async loadItemsAsync(showLoadingIndicator?: boolean): Promise<T[]> {
        let result: T[] = null;
        try {
            if (showLoadingIndicator === true) {
                this.eventService.useMethod('busy.on');
            }
            const queryResult: T[] = await this.dataServiceAccessor.getEntityDataService<T>(this.entityStoreFactory.dataAccessName.toLowerCase()).getAllAsync();
            this.clear();
            if ((UtilsMisc.isNullOrUndefined(queryResult) === false) && (queryResult.length > 0)) {
                result = queryResult;
                this.upsertItemsToStore(result);
            }
        } catch (error) {
        } finally {
            if (showLoadingIndicator === true) {
                this.eventService.useMethod('busy.off');
            }
        }

        return result;
    }

    public loadItemsWithPaging(startRow: number, rowsToTake: number, showLoadingIndicator: boolean = true): void {
        this.store.dispatch(this.entityStoreFactory.getAction('load.all.paging')({ startRow: startRow, rowsToTake: rowsToTake, showLoadingIndicator: showLoadingIndicator }));
        /*return this.dataServiceAccessor.getEntityDataService<T>(this.modelName.toLowerCase()).getAll({ startRow: startRow, rowsToTake: rowsToTake })
            .pipe(map((queryResult: IListResult<T>) => {
                if ((UtilsMisc.isNullOrUndefinedOrEmpty(queryResult) === false) && (UtilsMisc.isNullOrUndefined(queryResult.items) === false) &&
                    (queryResult.items.length > 0)) {
                    this.upsertItemsToStore(queryResult.items);
                }
                return queryResult;
            }));*/
    }

    public loadItemById$(id: number, showLoadingIndicator: boolean = true): Observable<T> {
        this.store.dispatch(this.entityStoreFactory.getAction('load.single')({ id: id, showLoadingIndicator: showLoadingIndicator }));

        return this.getItemById$(id);
    }

    public async loadItemByIdAsync(id: number, showLoadingIndicator: boolean = true): Promise<T> {
        let result: T = null;

        try {
            if (showLoadingIndicator === true) {
                this.eventService.useMethod('busy.on');
            }
            result = await this.dataServiceAccessor.getEntityDataService<T>(this.entityStoreFactory.dataAccessName.toLowerCase()).getByIdAsync(id);
            if (UtilsMisc.isNullOrUndefined(result) === false) {
                this.upsertItemToStore(result);
            }
        } catch (error) {
        } finally {
            if (showLoadingIndicator === true) {
                this.eventService.useMethod('busy.off');
            }
        }

        return result;
    }

    public saveItem(item: T, showLoadingIndicator: boolean = true): void {
        this.doSaveItem(item, showLoadingIndicator);
    }

    public saveItem$(item: T, showLoadingIndicator: boolean = true): Observable<T> {
        return this.doSaveItem$(item, showLoadingIndicator);
    }

    public async saveItemAsync(item: T, showLoadingIndicator: boolean = true): Promise<T> {
        return (await this.doSaveItemAsync(item, showLoadingIndicator));
    }

    public selectItem$(item: T): Observable<T> {
        if (item !== this.selectedItem) {
            this.store.dispatch(this.entityStoreFactory.getAction('set.selected')({ item: item }));
            this.selectedItem = item;
        }

        return this.selectedItem$;
    }

    public selectItemById$(id: number): Observable<T> {
        if (id !== this.selectedItemId) {
            this.store.dispatch(this.entityStoreFactory.getAction('set.selected.by.id')({ id: id }));
        }

        return this.getItemById$(id);
    }

    public upsertItemToStore(item: T): void {
        this.doUpsertItemToStore(item);
    }

    public upsertItemsToStore(items: T[]): void {
        if ((items != null) && (items.length > 0)) {
            this.store.dispatch(this.entityStoreFactory.getAction('upsert.multi')({ items: items }));
        }
    }

    protected doSaveItem(item: T, showLoadingIndicator: boolean = true): void {
        this.store.dispatch(this.entityStoreFactory.getAction('save.single')({ item: item, showLoadingIndicator: showLoadingIndicator }));
    }

    protected doSaveItem$(item: T, showLoadingIndicator: boolean = true): Observable<T> {
        this.store.dispatch(this.entityStoreFactory.getAction('save.single')({ item: item, showLoadingIndicator: showLoadingIndicator }));

        return this.getItemById$(item.id);
    }

    protected async doSaveItemAsync(item: T, showLoadingIndicator: boolean = true): Promise<T> {
        let result: T = item;
        try {
            if (showLoadingIndicator === true) {
                this.eventService.useMethod('busy.on');
            }

            result = (((await this.dataServiceAccessor.getEntityDataService(this.entityStoreFactory.dataAccessName.toLowerCase()).saveAsync(item)) as any) as T);
            this.store.dispatch(this.entityStoreFactory.getAction('upsert.single')({ item: result }));
        } catch (error) {
            throw error;
        } finally {
            if (showLoadingIndicator === true) {
                this.eventService.useMethod('busy.off');
            }
        }

        return result;
    }

    protected doUpsertItemToStore(item: T): void {
        if (item != null) {
            this.store.dispatch(this.entityStoreFactory.getAction('upsert.single')({ item: item }));
        }
    }

}
